import { useState, useCallback, useEffect, useRef } from 'react'
import type {
  DirectionsContext,
  DirectionsResult,
  TravelMode,
  PlaceAutocompleteResult,
} from '@/types/directions'
import { loadPlacesLibrary, isGoogleMapsLoaded, getPlacesLibrary, getMapsLoadError } from '@/lib/googleMaps'
import { placesAutocomplete, placeDetails } from '@/lib/placesRest'

interface UseDirectionsResult {
  // State
  isCalculating: boolean
  error: string | null
  result: DirectionsResult | null
  /** Why the last place lookup failed (key rejected, script blocked, …), if it did. */
  placesError: string | null

  // Actions
  calculateRoute: (context: DirectionsContext) => Promise<DirectionsResult | null>
  searchPlaces: (query: string) => Promise<PlaceAutocompleteResult[]>
  getPlaceDetails: (placeId: string) => Promise<{ address: string; name: string } | null>
  openInMaps: (context: DirectionsContext) => void
  /** Pure URL builder for a route — render into an <a target="_blank">. */
  buildMapsUrl: (context: DirectionsContext) => string
}

// Placeholder origin labels that are NOT real starting points. When the origin
// matches one of these we treat it as "no origin set": the route preview is
// skipped and the Maps URL omits origin so Google Maps falls back to the
// device's current location.
const ORIGIN_PLACEHOLDERS = new Set([
  'Tap to set your starting address',
  'Tap to set your home address', // legacy default
  'Your current location',
])

export function hasUsableOrigin(address: string | undefined | null): boolean {
  return !!address && address.trim().length > 0 && !ORIGIN_PLACEHOLDERS.has(address.trim())
}

/**
 * Hook for Google Maps/Directions integration
 * Updated to use Places API (New) - December 2024
 * Uses google.maps.importLibrary() for proper Places API loading
 * Note: Requires VITE_GOOGLE_MAPS_API_KEY environment variable
 */
export function useDirections(): UseDirectionsResult {
  const [isCalculating, setIsCalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<DirectionsResult | null>(null)
  const [sdkReady, setSdkReady] = useState(isGoogleMapsLoaded())
  const [placesError, setPlacesError] = useState<string | null>(getMapsLoadError())
  const placesLibraryRef = useRef<google.maps.PlacesLibrary | null>(getPlacesLibrary())

  // Load Google Maps SDK and Places library on mount
  useEffect(() => {
    if (sdkReady && placesLibraryRef.current) return

    loadPlacesLibrary()
      .then((lib) => {
        placesLibraryRef.current = lib
        setSdkReady(true)
      })
      .catch((err) => {
        console.error('Failed to load Google Maps SDK:', err)
        setPlacesError(getMapsLoadError() ?? (err instanceof Error ? err.message : 'Failed to load Google Maps'))
      })
  }, [sdkReady])

  // Google's auth/script failures arrive asynchronously (window.gm_authFailure,
  // script onerror) — listen so the field can surface the reason even if it
  // fires after the first search resolved empty.
  useEffect(() => {
    const onMapsError = (e: Event) => setPlacesError((e as CustomEvent<string>).detail)
    window.addEventListener('symphony:maps-error', onMapsError)
    return () => window.removeEventListener('symphony:maps-error', onMapsError)
  }, [])

  // =============================================================================
  // LEGACY SERVICE INITIALIZATION (commented out - kept for reference)
  // =============================================================================
  // import { useRef } from 'react' // Add to imports if reverting
  //
  // const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  // const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)
  // const placesServiceDivRef = useRef<HTMLDivElement | null>(null)
  //
  // useEffect(() => {
  //   if (!sdkReady) return
  //
  //   if (!autocompleteServiceRef.current) {
  //     autocompleteServiceRef.current = new google.maps.places.AutocompleteService()
  //   }
  //
  //   if (!placesServiceRef.current) {
  //     if (!placesServiceDivRef.current) {
  //       placesServiceDivRef.current = document.createElement('div')
  //     }
  //     placesServiceRef.current = new google.maps.places.PlacesService(placesServiceDivRef.current)
  //   }
  // }, [sdkReady])
  // =============================================================================

  // Calculate route using Google Directions Service (client-side API)
  const calculateRoute = useCallback(async (context: DirectionsContext): Promise<DirectionsResult | null> => {
    setIsCalculating(true)
    setError(null)

    try {
      // Check if origin has a real address (not a placeholder label).
      if (!hasUsableOrigin(context.origin.address)) {
        // No starting point set - show helpful message (the Maps handoff still
        // works without one; Google Maps uses the device's current location).
        setError('Set your starting address to see estimated travel time')
        setResult(null)
        return null
      }

      // Check if SDK is ready
      if (!sdkReady || !window.google?.maps?.DirectionsService) {
        setError('Maps service not ready')
        setResult(null)
        return null
      }

      // Map travel mode to Google API format
      const travelModeMap: Record<TravelMode, google.maps.TravelMode> = {
        driving: google.maps.TravelMode.DRIVING,
        walking: google.maps.TravelMode.WALKING,
        bicycling: google.maps.TravelMode.BICYCLING,
        transit: google.maps.TravelMode.TRANSIT,
      }

      // Build waypoints for intermediate stops
      const sortedStops = [...context.stops].sort((a, b) => a.order - b.order)
      const waypoints: google.maps.DirectionsWaypoint[] = sortedStops.map(stop => ({
        location: stop.address,
        stopover: true,
      }))

      // Create directions service and make request
      const directionsService = new google.maps.DirectionsService()

      const response = await directionsService.route({
        origin: context.origin.address,
        destination: context.destination.address,
        waypoints: waypoints.length > 0 ? waypoints : undefined,
        travelMode: travelModeMap[context.travelMode],
        optimizeWaypoints: false, // Keep order as specified
      })

      // Promise-based API throws on error, so if we get here status is OK
      if (!response.routes?.[0]) {
        setError('Could not calculate route')
        setResult(null)
        return null
      }

      const route = response.routes[0]

      // Extract leg information
      const legs: DirectionsResult['legs'] = route.legs.map(leg => ({
        startAddress: leg.start_address || '',
        endAddress: leg.end_address || '',
        duration: leg.duration?.value || 0, // seconds
        distance: leg.distance?.value || 0, // meters
      }))

      // Calculate totals
      const totalDuration = legs.reduce((sum, leg) => sum + leg.duration, 0)
      const totalDistance = legs.reduce((sum, leg) => sum + leg.distance, 0)

      const directionsResult: DirectionsResult = {
        duration: totalDuration,
        distance: totalDistance,
        legs,
      }

      setResult(directionsResult)
      return directionsResult
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to calculate route'
      setError(message)
      return null
    } finally {
      setIsCalculating(false)
    }
  }, [sdkReady])

  // =============================================================================
  // NEW PLACES API - searchPlaces using AutocompleteSuggestion
  // =============================================================================
  const searchPlaces = useCallback(async (query: string): Promise<PlaceAutocompleteResult[]> => {
    if (!query.trim()) return []

    try {
      // Use the REST endpoint over fetch, NOT the Maps JS SDK's
      // AutocompleteSuggestion: the SDK routes autocomplete through a gRPC-web
      // transport (places.googleapis.com/$rpc/.../AutocompletePlaces) that fails
      // on some devices/networks ("Rpc failed due to xhr error"). REST is
      // CORS-enabled and works where the gRPC transport is blocked.
      const results = await placesAutocomplete(query.trim())
      setPlacesError(null)
      return results
    } catch (err) {
      console.warn('Places autocomplete error:', err)
      // Surface the reason so the field shows it instead of a blank "No places found".
      setPlacesError(getMapsLoadError() ?? (err instanceof Error ? err.message : 'Place lookup failed'))
      return []
    }
  }, [])

  // =============================================================================
  // LEGACY searchPlaces (commented out - kept for reference)
  // =============================================================================
  // const searchPlaces = useCallback(async (query: string): Promise<PlaceAutocompleteResult[]> => {
  //   if (!query.trim()) return []
  //
  //   if (!autocompleteServiceRef.current) {
  //     console.warn('Google Maps SDK not ready for autocomplete')
  //     return []
  //   }
  //
  //   return new Promise((resolve) => {
  //     autocompleteServiceRef.current!.getPlacePredictions(
  //       {
  //         input: query.trim(),
  //         types: ['address', 'establishment'],
  //       },
  //       (predictions, status) => {
  //         if (
  //           status === google.maps.places.PlacesServiceStatus.OK &&
  //           predictions
  //         ) {
  //           resolve(
  //             predictions.map((p) => ({
  //               placeId: p.place_id,
  //               description: p.description,
  //               mainText: p.structured_formatting.main_text,
  //               secondaryText: p.structured_formatting.secondary_text || '',
  //             }))
  //           )
  //         } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
  //           resolve([])
  //         } else {
  //           console.warn('Places autocomplete error:', status)
  //           resolve([])
  //         }
  //       }
  //     )
  //   })
  // }, [])
  // =============================================================================

  // =============================================================================
  // NEW PLACES API - getPlaceDetails using Place.fetchFields
  // =============================================================================
  const getPlaceDetails = useCallback(async (placeId: string): Promise<{ address: string; name: string } | null> => {
    try {
      // REST place details (same transport reasoning as searchPlaces).
      const details = await placeDetails(placeId)
      return details ? { address: details.address, name: details.name } : null
    } catch (err) {
      console.warn('Place details error:', err)
      return null
    }
  }, [])

  // =============================================================================
  // LEGACY getPlaceDetails (commented out - kept for reference)
  // =============================================================================
  // const getPlaceDetails = useCallback(async (placeId: string): Promise<{ address: string; name: string } | null> => {
  //   if (!placesServiceRef.current) {
  //     console.warn('Google Maps SDK not ready for place details')
  //     return null
  //   }
  //
  //   return new Promise((resolve) => {
  //     placesServiceRef.current!.getDetails(
  //       {
  //         placeId,
  //         fields: ['formatted_address', 'name'],
  //       },
  //       (place, status) => {
  //         if (
  //           status === google.maps.places.PlacesServiceStatus.OK &&
  //           place
  //         ) {
  //           resolve({
  //             address: place.formatted_address || '',
  //             name: place.name || '',
  //           })
  //         } else {
  //           console.warn('Place details error:', status)
  //           resolve(null)
  //         }
  //       }
  //     )
  //   })
  // }, [])
  // =============================================================================

  // Build the Google Maps directions URL for a route. Pure — render it into an
  // <a target="_blank"> rather than calling window.open: on iOS a programmatic
  // window.open spawns a new tab that redirects into the Maps app, stranding the
  // user on a blank leftover tab when they come back. A real anchor lets the OS
  // hand the universal link to Maps and return cleanly to Symphony.
  const buildMapsUrl = useCallback((context: DirectionsContext): string => {
    const destination = encodeURIComponent(context.destination.address)

    // Build waypoints for multi-stop routes
    const waypoints = context.stops
      .sort((a, b) => a.order - b.order)
      .map(stop => encodeURIComponent(stop.address))
      .join('|')

    let url = `https://www.google.com/maps/dir/?api=1`
    // Only pin an origin when there's a real starting point. Without one, Google
    // Maps uses the device's current location — far better than routing from the
    // literal placeholder text.
    if (hasUsableOrigin(context.origin.address)) {
      url += `&origin=${encodeURIComponent(context.origin.address)}`
    }
    url += `&destination=${destination}`

    if (waypoints) {
      url += `&waypoints=${waypoints}`
    }

    const travelModeMap: Record<TravelMode, string> = {
      driving: 'driving',
      walking: 'walking',
      bicycling: 'bicycling',
      transit: 'transit',
    }
    url += `&travelmode=${travelModeMap[context.travelMode]}`
    return url
  }, [])

  // Open route in Google Maps app/web (non-anchor callers).
  const openInMaps = useCallback((context: DirectionsContext) => {
    window.open(buildMapsUrl(context), '_blank', 'noopener')
  }, [buildMapsUrl])

  return {
    isCalculating,
    error,
    result,
    placesError,
    calculateRoute,
    searchPlaces,
    getPlaceDetails,
    openInMaps,
    buildMapsUrl,
  }
}

/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes} min`
}

/**
 * Format distance in meters to human-readable string
 */
export function formatDistance(meters: number): string {
  const miles = meters / 1609.344
  if (miles >= 10) {
    return `${Math.round(miles)} mi`
  }
  return `${miles.toFixed(1)} mi`
}
